import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Loader2, Upload } from "lucide-react";
import { submitInsuranceQuote, uploadInsuranceBinder } from "@/Calls/callInsuranceAuth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";

const COVERAGE_TYPES = [
    "Liability Only",
    "Comprehensive & Collision",
    "Full Coverage",
    "State Minimum",
    "Commercial Auto",
    "Motorcycle",
    "GAP Coverage",
];

export default function InsuranceSubmitQuotePage() {
    const { requestId } = useParams<{ requestId: string }>();
    const navigate = useNavigate();
    const [form, setForm] = useState({
        coverage_type: "",
        premium_monthly: "",
        premium_annual: "",
        deductible: "",
        policy_number: "",
        coverage_start: "",
        coverage_end: "",
        notes: "",
    });
    const [binderNumber, setBniderNumber] = useState("");
    const [binderFile, setBinderFile] = useState<File | null>(null);
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!requestId) return;
        setLoading(true);
        setError(null);
        try {
            const res = await submitInsuranceQuote(Number(requestId), form);
            if (!res.ok) throw new Error(res.message);

            if (binderFile && res.quote?.quote_id) {
                await uploadInsuranceBinder(res.quote.quote_id, binderFile, binderNumber || undefined);
            }
            setSuccess(true);
        } catch (e: any) {
            setError(e.message ?? "Failed to submit quote");
        } finally {
            setLoading(false);
        }
    };

    if (success) {
        return (
            <div className="max-w-lg mx-auto text-center py-20">
                <div className="text-5xl mb-4">✅</div>
                <h2 className="text-2xl font-semibold mb-2">Quote Submitted</h2>
                <p className="text-muted-foreground mb-6">Your quote has been submitted to the customer's deal jacket.</p>
                <Button onClick={() => navigate("/insurance/quote-requests")}>Back to Leads</Button>
            </div>
        );
    }

    return (
        <div className="max-w-2xl mx-auto space-y-6">
            <div>
                <h1 className="text-2xl font-light uppercase tracking-wide">Submit Quote</h1>
                <p className="text-muted-foreground text-sm mt-1">Request #{requestId}</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
                <Card className="p-5 space-y-4">
                    <h3 className="font-semibold">Coverage Details</h3>

                    <div>
                        <label className="text-sm font-medium">Coverage Type *</label>
                        <select
                            value={form.coverage_type}
                            onChange={(e) => setForm({ ...form, coverage_type: e.target.value })}
                            required
                            className="mt-1 block w-full rounded-lg border bg-background px-3 py-2 text-sm"
                        >
                            <option value="">Select coverage type</option>
                            {COVERAGE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                        </select>
                    </div>

                    <div className="grid sm:grid-cols-2 gap-4">
                        <div>
                            <label className="text-sm font-medium">Monthly Premium ($)</label>
                            <Input
                                type="number" step="0.01" min="0"
                                value={form.premium_monthly}
                                onChange={(e) => setForm({ ...form, premium_monthly: e.target.value })}
                                placeholder="e.g. 125.00"
                                className="mt-1"
                            />
                        </div>
                        <div>
                            <label className="text-sm font-medium">Annual Premium ($)</label>
                            <Input
                                type="number" step="0.01" min="0"
                                value={form.premium_annual}
                                onChange={(e) => setForm({ ...form, premium_annual: e.target.value })}
                                placeholder="e.g. 1350.00"
                                className="mt-1"
                            />
                        </div>
                        <div>
                            <label className="text-sm font-medium">Deductible ($)</label>
                            <Input
                                type="number" step="1" min="0"
                                value={form.deductible}
                                onChange={(e) => setForm({ ...form, deductible: e.target.value })}
                                placeholder="e.g. 500"
                                className="mt-1"
                            />
                        </div>
                        <div>
                            <label className="text-sm font-medium">Policy Number</label>
                            <Input
                                value={form.policy_number}
                                onChange={(e) => setForm({ ...form, policy_number: e.target.value })}
                                placeholder="Optional"
                                className="mt-1"
                            />
                        </div>
                        <div>
                            <label className="text-sm font-medium">Coverage Start</label>
                            <Input
                                type="date"
                                value={form.coverage_start}
                                onChange={(e) => setForm({ ...form, coverage_start: e.target.value })}
                                className="mt-1"
                            />
                        </div>
                        <div>
                            <label className="text-sm font-medium">Coverage End</label>
                            <Input
                                type="date"
                                value={form.coverage_end}
                                onChange={(e) => setForm({ ...form, coverage_end: e.target.value })}
                                className="mt-1"
                            />
                        </div>
                    </div>

                    <div>
                        <label className="text-sm font-medium">Notes</label>
                        <textarea
                            value={form.notes}
                            onChange={(e) => setForm({ ...form, notes: e.target.value })}
                            rows={3}
                            className="mt-1 block w-full rounded-lg border bg-background px-3 py-2 text-sm resize-none"
                            placeholder="Additional coverage notes..."
                        />
                    </div>
                </Card>

                <Card className="p-5 space-y-4">
                    <h3 className="font-semibold">Binder (Optional)</h3>
                    <div className="grid sm:grid-cols-2 gap-4">
                        <div>
                            <label className="text-sm font-medium">Binder Number</label>
                            <Input
                                value={binderNumber}
                                onChange={(e) => setBniderNumber(e.target.value)}
                                placeholder="e.g. BND-2025-001"
                                className="mt-1"
                            />
                        </div>
                        <div>
                            <label className="text-sm font-medium">Binder PDF / Image</label>
                            <label className="mt-1 flex items-center gap-2 cursor-pointer rounded-lg border border-dashed p-3 hover:bg-muted transition-colors text-sm">
                                <Upload className="h-4 w-4 text-muted-foreground" />
                                {binderFile ? binderFile.name : "Upload binder document"}
                                <input
                                    type="file"
                                    accept=".pdf,.jpg,.jpeg,.png"
                                    className="hidden"
                                    onChange={(e) => setBinderFile(e.target.files?.[0] ?? null)}
                                />
                            </label>
                        </div>
                    </div>
                </Card>

                {error && <p className="text-red-500 text-sm">{error}</p>}

                <div className="flex gap-3">
                    <Button type="button" variant="outline" onClick={() => navigate(-1)}>Cancel</Button>
                    <Button
                        type="submit"
                        className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white"
                        disabled={loading}
                    >
                        {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                        Submit Quote
                    </Button>
                </div>
            </form>
        </div>
    );
}
